"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { TestimonialsPanel } from "@/components/testimonials-panel";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.message || "Erro ao enviar email de recuperação");
      }
    } catch {
      setError("Erro ao enviar email de recuperação");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 overflow-hidden">
        {/* Left Panel - Success Message */}
        <div className="w-full lg:w-2/5 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6 sm:p-8 lg:p-12 h-screen overflow-y-auto">
          <div className="w-full max-w-md space-y-8">
            <div>
              <div className="w-full mb-0 mt-[-50px] pb-0">
                    <Image
                      src="/logo-nova-vagaautomatica.png"
                      alt="Vaga Automática"
                      width={200}
                      height={100}
                      className="h-[160px] w-auto object-contain ml-[-20px] mb-[-40px]"
                      priority
                    />              
                </div>
              <h1 className="text-3xl font-bold text-gray-900">Email enviado!</h1>
              <p className="text-gray-600">Verifique sua caixa de entrada</p> 
            </div>

            {/* Success Message */}
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Link de recuperação enviado
                  </h2>
                  <p className="text-sm text-gray-600">
                    Enviamos um link de recuperação para <strong className="text-gray-900">{email}</strong>
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Verifique sua caixa de entrada e clique no link para redefinir sua senha.
                </p>
                <p className="text-xs text-gray-500">
                  Não recebeu o email? Verifique sua pasta de spam ou tente novamente.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail("");
                  }}
                  variant="outline"
                  className="w-full h-11"
                >
                  Tentar outro email
                </Button>
                
                <Link href="/" className="block">
                  <Button variant="ghost" className="w-full h-11">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <TestimonialsPanel />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 overflow-hidden">
      {/* Left Panel - Forgot Password Form */}
      <div className="w-full lg:w-2/5 bg-white/80 backdrop-blur-sm flex items-center justify-center p-6 sm:p-8 lg:p-12 h-screen overflow-y-auto">
        <div className="w-full max-w-md space-y-8">
            <div>
              <div className="w-full mb-0 mt-[-50px] pb-0">
                    <Image
                      src="/logo-nova-vagaautomatica.png"
                      alt="Vaga Automática"
                      width={200}
                      height={100}
                      className="h-[160px] w-auto object-contain ml-[-20px] mb-[-40px]"
                      priority
                    />              
                </div>
              <h1 className="text-3xl font-bold text-gray-900">Esqueceu sua senha?</h1>
              <p className="text-gray-600">Digite seu email para receber um link de recuperação</p> 
            </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Recuperar senha</h2>
              <p className="text-sm text-gray-600">
                Enviaremos um link seguro para seu email
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    className="pl-10 h-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="cursor-pointer w-full bg-primary from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-blue-500/30 transition-all duration-200 h-11"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  "Enviar link de recuperação"
                )}
              </Button>
            </form>

            <div className="text-center text-sm text-gray-600 pt-2">
              Lembrou da senha?{" "}
              <Link 
                href="/" 
                className="text-primary hover:text-blue-700 hover:underline font-semibold transition-colors"
              >
                Fazer login
              </Link>
            </div>

            <div className="text-center text-xs text-gray-500">
              Não tem uma conta?{" "}
              <Link href="/register" className="text-primary hover:text-blue-700 hover:underline font-medium transition-colors">
                Criar conta gratuita
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <TestimonialsPanel />
    </div>
  );
}